import { Component, Prop, h, Host, Element, Watch, Method } from '@stencil/core';
import getFocusableElements from '../../capabilities/HTMLElement/get-focusable-elements';
import elementIsViewable from '../../capabilities/HTMLElement/element-is-viewable';
import onChildrenReady from '../../capabilities/on-children-ready';

/**
 * This component creates a trap around focusable elements
 *
 * @description This component creates a focus trap around the elements inside it.
 * @use
 * @since 5.7.0
 * @slot - `default slot`: The inserted elements that will be surrounded by the focus trap.
 * @version NV
 */
@Component({
  tag: 'hp6-focus-trap',
  styleUrl: 'focus-trap.scss',
  shadow: true,
})
export class FocusTrap {
  /**
   * When true, the focus trap is active
   */
  @Prop() isActive: boolean = false;

  /**
   * Ref to this nodes el
   */
  @Element() private host: HTMLElement;

  /**
   * This is a "fake" redirecting DOM element that when focused,
   * redirects focus to this.secondFocusable or this.secondToLastFocusable
   */
  private firstFocusable: HTMLElement;

  /**
   * This is the first _real_ focusable element in this.ref
   * The first focusable element in this component's slot.
   * Likely the modal close button.
   */
  private secondFocusable: HTMLElement;

  /**
   * The second to last _real_ focusable element in this.ref.
   * The last focusable element in this component's slot.
   */
  private secondToLastFocusable: HTMLElement;

  /**
   * This is a "fake" redirecting DOM element that when focused,
   * redirects focus to this.secondFocusable or this.secondToLastFocusable
   */
  private lastFocusable: HTMLElement;

  /**
   * constructor()
   */
  constructor() {
    this.eventListenerFirst = this.eventListenerFirst.bind(this);
    this.eventListenerLast = this.eventListenerLast.bind(this);
    this.removeEventListeners = this.removeEventListeners.bind(this);
  }

  /**
   * Watch for when the active prop changes
   *
   * @param isActive boolean
   */
  @Watch('isActive')
  async onActiveChange(isActive: boolean) {
    if (isActive) {
      this.firstFocusable.setAttribute('tabIndex', '0');
      this.lastFocusable.setAttribute('tabIndex', '0');
    } else {
      this.firstFocusable.setAttribute('tabIndex', '-1');
      this.lastFocusable.setAttribute('tabIndex', '-1');

      return;
    }

    await onChildrenReady(this.host, () => {
      const focusableArrayHost = Array.from(getFocusableElements(this.host));

      const { firstFocusable, lastFocusable } = this;

      const focusableArray = [firstFocusable, ...focusableArrayHost, lastFocusable];

      let visibleArray = [];

      for (let i = 0; i < focusableArray.length; i++) {
        const el = focusableArray[i] as HTMLElement;

        const visible = elementIsViewable(window, el);

        if (visible) {
          visibleArray = [...visibleArray, el];
        }
      }

      const [, secondFocusable] = visibleArray;
      this.secondFocusable = secondFocusable;
      this.secondToLastFocusable = visibleArray[visibleArray.length - 2];

      if (this.secondFocusable) {
        this.secondFocusable.focus();
      }
    });
  }

  /**
   * This method is used when children inside of <slot> change, and
   * the focusable elements need to be updated (ex. task-flow-linear)
   *
   * TODO: replace this method with a Mutation Observer in parent molecules
   * https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
   */
  @Method()
  async refresh() {
    this.onActiveChange(this.isActive);
  }

  eventListenerFirst() {
    this.secondToLastFocusable.focus();
  }

  eventListenerLast() {
    this.secondFocusable.focus();
  }

  removeEventListeners() {
    this.firstFocusable.removeEventListener('focus', this.eventListenerFirst);
    this.lastFocusable.removeEventListener('focus', this.eventListenerLast);
  }

  componentDidLoad() {
    this.firstFocusable = this.host.shadowRoot.querySelector('.first-focusable');
    this.lastFocusable = this.host.shadowRoot.querySelector('.last-focusable');

    this.removeEventListeners();

    this.firstFocusable.addEventListener('focus', this.eventListenerFirst);
    this.lastFocusable.addEventListener('focus', this.eventListenerLast);

    this.onActiveChange(this.isActive);
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  /**
   * render()
   */
  render() {
    return (
      <Host>
        <span class="hidden-focusable">
          <div class="first-focusable" tabIndex={-1} />
        </span>

        <slot />

        <span class="hidden-focusable">
          <div class="last-focusable" tabIndex={-1} />
        </span>
      </Host>
    );
  }
}
